/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { init, send } from 'emailjs-com';
import Navbar from './Navbar';
import Footer from './Footer';
import { TbHome, TbBuilding, TbBriefcase, TbTools } from 'react-icons/tb';

// Initialize EmailJS with your user ID
init("d1zH6UNPIGfFtdCEu");

const CallbackPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    category: 'residential', // 'residential', 'housing-society', 'commercial', 'utility-scale'
    name: '',
    email: '',
    phone: '',
    preferredTime: '',
    message: '',
    contactMethod: 'callback', // 'callback' or 'direct'
    // Additional fields for different categories
    pincode: '',
    city: '',
    monthlyBill: '',
    // Housing Society specific
    societyName: '',
    designation: '',
    agmApproval: '',
    // Commercial specific
    companyName: '',
    averageBill: '',
    // Utility Scale specific
    utilityCompanyName: ''
  });

  const timeSlots = [
    '9:00 AM - 10:00 AM',
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM',
    '4:00 PM - 5:00 PM',
    '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM'
  ];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('callback-page');
    return () => {
      document.body.classList.remove('callback-page');
    };
  }, []);

  // SEO optimization - Update meta tags for this page
  useEffect(() => {
    document.title = 'Lunar Electric | Request Callback | Solar Panel Installation Services India';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Request a callback from Lunar Electric for professional solar panel installation services. Get expert consultation for residential, commercial, and utility-scale solar projects across India.');
    }
    
    // Add meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'lunar electric callback, solar consultation, solar panel installation quote, solar callback, lunar electric contact, solar panel consultation India');
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Lunar Electric | Request Callback | Solar Panel Installation Services');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Request a callback from Lunar Electric for professional solar panel installation services. Get expert consultation for residential, commercial, and utility-scale solar projects.');
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://lunarelectric.in/callback');
    }
    
    // Update Twitter tags
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', 'https://lunarelectric.in/callback');
    }
    
    // Update canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', 'https://lunarelectric.in/callback');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.contactMethod === 'direct') {
      // Handle direct call - your phone number
      window.open('tel:+919148857417', '_self');
    } else {
      // Handle callback request - send email using EmailJS
      const emailData = {
        name: formData.name,
        email: formData.email || 'Not provided',
        subject: formData.phone, // Phone number as subject
        message: `CALLBACK REQUEST DETAILS:
Preferred Time: ${formData.preferredTime}
Phone: ${formData.phone}
Email: ${formData.email || 'Not provided'}
Additional Message: ${formData.message || 'No additional message'}

This is a callback request from the Lunar Electric website.`
      };

      // Use EmailJS to send the email
      send('service_9uk4fgz', 'template_uuwsowl', emailData)
        .then((response) => {
          console.log('Callback request received', response.status, response.text);
          
          // Reset the form after successful submission
          setFormData({
            name: '',
            email: '',
            phone: '',
            preferredTime: '',
            message: '',
            contactMethod: 'callback'
          });
          alert('Thank you! We will call you back at your preferred time.');
        })
        .catch((err) => {
          console.error('Failed to send callback request', err);
          alert('Failed to send callback request, please try again.');
        });
    }
  };

  return (
    <>
      <Navbar />
      <div className="callback-page">
        <div className="callback-hero">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h1 className="callback-title">Go Solar Today</h1>
                <p className="callback-subtitle">Get your free solar consultation - residential or commercial</p>
              </div>
            </div>
          </div>
        </div>

        <div className="callback-content">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6 col-md-8 callback-form-container">
                <div className="callback-form-wrapper">
                  <h2 className="form-title">Get a Free Quote for Rooftop Solar</h2>
                  <p className="form-subtitle">Schedule a FREE consultation with us today!</p>
                  
                  {/* Category Selection - Compact */}
                  <div className="category-selection-compact">
                    <h3 className="category-section-title">Choose Your Project Type</h3>
                    <div className="category-options-compact">
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-residential"
                          name="category"
                          value="residential"
                          checked={formData.category === 'residential'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-residential" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbHome size={20} />
                          </span>
                          <span className="category-text">Residential</span>
                        </label>
                      </div>
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-housing-society"
                          name="category"
                          value="housing-society"
                          checked={formData.category === 'housing-society'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-housing-society" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbBuilding size={20} />
                          </span>
                          <span className="category-text">Housing Society</span>
                        </label>
                      </div>
                      <div className="category-option-compact">
                        <input
                          type="radio"
                          id="category-commercial"
                          name="category"
                          value="commercial"
                          checked={formData.category === 'commercial'}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="category-commercial" className="category-label-compact">
                          <span className="category-icon-compact">
                            <TbBriefcase size={20} />
                          </span>
                          <span className="category-text">Commercial</span>
                        </label>
                      </div>
<div className="category-option-compact">
  <input
    type="radio"
    id="category-utility-scale"
    name="category"
    value="utility-scale"
    checked={formData.category === 'utility-scale'}
    onChange={handleInputChange}
  />
  <label htmlFor="category-utility-scale" className="category-label-compact">
    <span className="category-icon-compact">
      <TbTools size={20} />
    </span>
    <span className="category-text">Utility Scale</span>
  </label>
</div>
                    </div>
                  </div>
                  
                  {/* Form */}
                  <form onSubmit={handleSubmit} className="callback-form-compact">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    {/* Conditional fields based on category */}
                    {formData.category !== 'utility-scale' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="pincode">Pin Code *</label>
                          <input
                            type="text"
                            id="pincode"
                            name="pincode"
                            value={formData.pincode}
                            onChange={handleInputChange}
                            className="form-input"
                            pattern="[0-9]{6}"
                            title="Please enter a valid 6-digit pin code"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="city">City *</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {formData.category === 'utility-scale' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="email">Email</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="utilityCompanyName">Company Name</label>
                          <input
                            type="text"
                            id="utilityCompanyName"
                            name="utilityCompanyName"
                            value={formData.utilityCompanyName}
                            onChange={handleInputChange}
                            className="form-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Category-specific fields */}
                      {formData.category === 'residential' && (
                        <div className="form-group">
                          <label htmlFor="monthlyBill">What is your average monthly electricity bill? *</label>
                          <select
                            id="monthlyBill"
                            name="monthlyBill"
                            value={formData.monthlyBill}
                            onChange={handleInputChange}
                            className="form-select"
                            required
                          >
                            <option value="">Select your monthly bill</option>
                            <option value="less-than-1500">Less than ₹1,500</option>
                            <option value="1500-2500">₹1,500 - ₹2,500</option>
                            <option value="2500-4000">₹2,500 - ₹4,000</option>
                            <option value="4000-8000">₹4,000 - ₹8,000</option>
                            <option value="more-than-8000">More than ₹8,000</option>
                          </select>
                        </div>
                      )}

                      {formData.category === 'housing-society' && (
                        <>
                          <div className="form-group">
                            <label htmlFor="societyName">Name of Housing Society *</label>
                            <input
                              type="text"
                              id="societyName"
                              name="societyName"
                              value={formData.societyName}
                              onChange={handleInputChange}
                              className="form-input"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="designation">What is your designation in Housing Society? *</label>
                            <select
                              id="designation"
                              name="designation"
                              value={formData.designation}
                              onChange={handleInputChange}
                              className="form-select"
                              required
                            >
                              <option value="">Select your designation</option>
                              <option value="management-committee">Management committee member</option>
                              <option value="resident">Resident</option>
                              <option value="builder">Builder</option>
                              <option value="facility-manager">Facility Manager</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label htmlFor="agmApproval">AGM approval status *</label>
                            <select
                              id="agmApproval"
                              name="agmApproval"
                              value={formData.agmApproval}
                              onChange={handleInputChange}
                              className="form-select"
                              required
                            >
                              <option value="">Select approval status</option>
                              <option value="approved">We already have AGM approval</option>
                              <option value="pending">We don't have an AGM approval yet</option>
                              <option value="help">We want help in preparing for our AGM</option>
                            </select>
                          </div>
                        </>
                      )}

                      {formData.category === 'commercial' && (
                        <>
                          <div className="form-group">
                            <label htmlFor="companyName">Company Name *</label>
                            <input
                              type="text"
                              id="companyName"
                              name="companyName"
                              value={formData.companyName}
                              onChange={handleInputChange}
                              className="form-input"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="averageBill">Average Monthly Bill *</label>
                            <select
                              id="averageBill"
                              name="averageBill"
                              value={formData.averageBill}
                              onChange={handleInputChange}
                              className="form-select"
                              required
                            >
                              <option value="">Select your average monthly bill</option>
                              <option value="0-50000">₹0 - ₹50,000</option>
                              <option value="50000-2lacs">₹50,000 - ₹2,00,000</option>
                              <option value="more-than-2lacs">More than ₹2,00,000</option>
                            </select>
                          </div>
                        </>
                      )}

                      <div className="form-group">
                        <label htmlFor="preferredTime">Preferred Time Slot *</label>
                        <select
                          id="preferredTime"
                          name="preferredTime"
                          value={formData.preferredTime}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select a time slot</option>
                          {timeSlots.map((slot, index) => (
                            <option key={index} value={slot}>{slot}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Message (Optional)</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          className="form-textarea"
                          rows="4"
                          placeholder="Tell us about your solar requirements (residential/commercial)..."
                        />
                      </div>

                    <div className="form-submit-section">
                      <button type="submit" className="submit-btn-compact">
                        Get Solar Quote
                      </button>
                      <a href="tel:+919148857417" className="call-now-btn">
                        📞 Call Now
                      </a>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="callback-cta">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h2>Ready to Go Solar?</h2>
                <p>Don't wait - contact us today for professional solar rooftop installations</p>
                <Link to="/" className="btn btn-home">
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CallbackPage;